/* ============================================================
   FixOEM: lógica REAL de "precio de mayoreo" para el banner del carrito.
   =============================================================
   ANTES: en 2 lugares distintos de este archivo (updateAllCart y
   updateDrawerSummary) el banner/barra se calculaba con un umbral fijo de
   $4,500 de SUBTOTAL de carrito -- sin importar qué productos fueran. Eso
   no tiene nada que ver con cómo funciona el mayoreo real en la tienda: el
   mayoreo se maneja POR PIEZAS dentro de cada categoría (la mayoría 5
   piezas), configurado en la app Samita Wholesale.

   AHORA: esta función lee en vivo las reglas de
   window.Samita.Wholesale.volumePricing (sin hardcodear categorías ni
   umbrales -- si cambian el número de piezas o agregan categorías en el
   panel de la app, esto se actualiza solo), cruza contra /cart.js para ver
   cuántas piezas de cada categoría hay en el carrito, y arma el mensaje
   correcto, ej.:
     "Ya tienes -4% de mayoreo en Display · te faltan 10 piezas para -7%"

   Se llama desde TODOS los lugares de este archivo que antes escribían el
   mensaje de $4,500 (updateAllCart, updateDrawerSummary) y también desde los
   3 mecanismos de sincronización que reconstruyen el carrito por su cuenta
   (FixOEMCartDrawerSyncV3, FixOEMCartBadgeObserverV1, FixOEMCartLiveSyncV1)
   para que el mensaje correcto sobreviva sin importar cuál de los 5 scripts
   de este archivo termine de pintar el carrito al final.
   ============================================================ */
(function () {
  'use strict';

  var CACHE_KEY = 'fixoem_wholesale_collection_map_v1';
  var CACHE_TTL_MS = 10 * 60 * 1000; // 10 min -- las colecciones casi no cambian en caliente

  function getWholesaleConfig() {
    var w = window.Samita && window.Samita.Wholesale;
    if (!w) return null;
    return {
      shopUrl: w.shop && w.shop.url,
      token: w.shop && w.shop.storefront_access_token,
      rules: (w.volumePricing || []).filter(function (e) {
        return e && e.type === 'quantity' && e.configs && e.configs.volume_discount &&
          Array.isArray(e.configs.volume_discount.discounts) && e.configs.volume_discount.discounts.length;
      })
    };
  }

  function normalizedTiers(rule) {
    return rule.configs.volume_discount.discounts
      .map(function (d) {
        return { fromQty: parseInt(d.from_qty != null ? d.from_qty : d.qty, 10), value: parseFloat(d.value) };
      })
      .filter(function (t) { return Number.isFinite(t.fromQty) && t.fromQty > 0; })
      .sort(function (a, b) { return a.fromQty - b.fromQty; });
  }

  function fetchCollectionProductIds(shopUrl, token, collectionIds) {
    if (!collectionIds.length) return Promise.resolve({});
    var aliases = collectionIds.map(function (id, i) {
      return 'c' + i + ': collection(id: "gid://shopify/Collection/' + id + '") { title products(first: 100) { edges { node { id } } } }';
    }).join('\n');
    var query = '{ ' + aliases + ' }';
    return fetch('https://' + shopUrl + '/api/2025-10/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query: query })
    }).then(function (resp) { return resp.json(); }).then(function (json) {
      var map = {};
      collectionIds.forEach(function (id, i) {
        var node = json.data && json.data['c' + i];
        if (!node) return;
        map[id] = {
          title: node.title,
          // el gid viene como "gid://shopify/Product/123456" -- se toma el
          // número final para comparar contra item.product_id de /cart.js
          productIds: (node.products.edges || []).reduce(function (set, e) {
            set[e.node.id.split('/').pop()] = true;
            return set;
          }, {})
        };
      });
      return map;
    });
  }

  function getCollectionMap(shopUrl, token, collectionIds) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
        var map = {};
        var ok = true;
        collectionIds.forEach(function (id) {
          if (cached.data[id]) map[id] = cached.data[id];
          else ok = false;
        });
        if (ok) return Promise.resolve(map);
      }
    } catch (e) { /* cache corrupto, ignorar */ }

    return fetchCollectionProductIds(shopUrl, token, collectionIds).then(function (fresh) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: fresh }));
      } catch (e) { /* sessionStorage lleno o bloqueado, no es crítico */ }
      return fresh;
    });
  }

  function ruleLabel(rule, collectionMap) {
    var applyProduct = rule.product_configs && rule.product_configs.apply_product;
    var applyType = applyProduct && applyProduct.type;
    if (applyType === 'collections') {
      var ids = (applyProduct.collections && applyProduct.collections.ids) || [];
      for (var i = 0; i < ids.length; i++) {
        if (collectionMap[ids[i]] && collectionMap[ids[i]].title) return collectionMap[ids[i]].title;
      }
      return 'esta categoría';
    }
    return 'estos productos';
  }

  function matchesRule(rule, productId, collectionMap) {
    var applyProduct = rule.product_configs && rule.product_configs.apply_product;
    if (!applyProduct) return false;
    if (applyProduct.type === 'products') {
      var ids = (applyProduct.manual && applyProduct.manual.ids) || [];
      return ids.map(String).indexOf(String(productId)) !== -1;
    }
    if (applyProduct.type === 'collections') {
      var collIds = (applyProduct.collections && applyProduct.collections.ids) || [];
      for (var i = 0; i < collIds.length; i++) {
        var entry = collectionMap[collIds[i]];
        if (entry && entry.productIds[String(productId)]) return true;
      }
      return false;
    }
    return false;
  }

  function computeBestProgress(cart) {
    var config = getWholesaleConfig();
    if (!config || !config.rules.length) return Promise.resolve(null);
    if (!cart.items.length) return Promise.resolve(null);

    var collectionIds = [];
    config.rules.forEach(function (r) {
      var applyProduct = r.product_configs && r.product_configs.apply_product;
      if (applyProduct && applyProduct.type === 'collections') {
        (applyProduct.collections.ids || []).forEach(function (id) {
          if (collectionIds.indexOf(id) === -1) collectionIds.push(id);
        });
      }
    });

    return getCollectionMap(config.shopUrl, config.token, collectionIds).then(function (collectionMap) {
      var best = null;

      config.rules.forEach(function (rule) {
        var tiers = normalizedTiers(rule);
        if (!tiers.length) return;

        var qtyInCategory = 0;
        cart.items.forEach(function (item) {
          if (matchesRule(rule, item.product_id, collectionMap)) qtyInCategory += item.quantity;
        });
        if (qtyInCategory <= 0) return;

        var label = ruleLabel(rule, collectionMap);
        // último tier que ya se alcanzó con la cantidad actual (o -1 si aún
        // no llega ni al primero)
        var currentTierIdx = -1;
        for (var i = 0; i < tiers.length; i++) {
          if (qtyInCategory >= tiers[i].fromQty) currentTierIdx = i;
        }
        var nextTier = null;
        for (var j = 0; j < tiers.length; j++) {
          if (qtyInCategory < tiers[j].fromQty) { nextTier = tiers[j]; break; }
        }
        var currentTier = currentTierIdx >= 0 ? tiers[currentTierIdx] : null;

        if (!nextTier) {
          // ya alcanzó el tier más alto disponible en esta categoría
          var doneCandidate = { done: true, label: label, qtyInCategory: qtyInCategory, maxDiscount: currentTier.value };
          if (!best || !best.done) best = doneCandidate;
          return;
        }

        var remaining = nextTier.fromQty - qtyInCategory;
        var percent = Math.min(100, Math.round((qtyInCategory / nextTier.fromQty) * 100));
        var candidate = { done: false, label: label, qtyInCategory: qtyInCategory, remaining: remaining, nextTier: nextTier, currentTier: currentTier, percent: percent };

        // preferimos la categoría más cerca de desbloquear el siguiente nivel
        if (!best || best.done || remaining < best.remaining) best = candidate;
      });

      return best;
    });
  }

  function renderBest(best) {
    var drawer = document.querySelector('.fixoem-cart-drawer, .shoplist');
    if (!drawer) return;
    var progressWrap = drawer.querySelector('.fixoem-cart-drawer__progress, .timer');
    // FixOEM: el texto vive en #timer-message / .timer-message, HERMANO de
    // ".progress-container" -- nunca se debe sobreescribir el
    // progressWrap.innerHTML completo porque eso borra la barra de progreso
    // de paso (bug real que se encontró probando esto en vivo).
    var textEl = progressWrap && (progressWrap.querySelector('#timer-message') || progressWrap.querySelector('.timer-message'));
    var bar = drawer.querySelector('.progress-bar');
    var barContainer = drawer.querySelector('.progress-container');

    if (!progressWrap || !textEl || !bar || !barContainer) return;

    if (!best) {
      // No hay productos con una regla mayorista activa en el carrito.
      // Mostramos una invitación general sin prometer envío gratis ni un descuento específico.
      progressWrap.style.display = '';
      textEl.textContent = 'Compra más y ahorra más con nuestros precios de mayoreo';
      bar.style.width = '0%';
      barContainer.setAttribute('aria-valuenow', '0');
      return;
    }
    progressWrap.style.display = '';

    if (best.done) {
      textEl.textContent = '¡Ya tienes precio de mayoreo en ' + best.label + '! (-' + best.maxDiscount + '%)';
      bar.style.width = '100%';
      barContainer.setAttribute('aria-valuenow', '100');
    } else {
      var piezaTexto = best.remaining === 1 ? 'pieza' : 'piezas';
      var faltanTexto = 'Te ' + (best.remaining === 1 ? 'falta' : 'faltan') + ' ' + best.remaining + ' ' + piezaTexto + ' de ' + best.label + ' para -' + best.nextTier.value + '% de mayoreo';
      textEl.textContent = best.currentTier
        ? 'Ya tienes -' + best.currentTier.value + '% de mayoreo en ' + best.label + ' · ' + faltanTexto.charAt(0).toLowerCase() + faltanTexto.slice(1)
        : faltanTexto;
      bar.style.width = best.percent + '%';
      barContainer.setAttribute('aria-valuenow', String(best.percent));
    }
  }

  var pending = null;
  function updateWholesaleProgress() {
    // evita carreras si se llama varias veces seguidas (varios scripts de
    // sincronización pueden dispararla casi al mismo tiempo)
    if (pending) return pending;
    pending = fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { return computeBestProgress(cart); })
      .then(function (best) { renderBest(best); })
      .catch(function (err) { console.warn('[FixOEM mayoreo]', err); })
      .finally(function () { pending = null; });
    return pending;
  }

  window.FixOEMUpdateWholesaleProgress = updateWholesaleProgress;
})();


document.addEventListener("DOMContentLoaded", function () {
  let cartlist = document.querySelector(".shoplist");
  let popUpComponent = document.querySelector("#cart-message");

  function openPopUpComponent() {
    popUpComponent.classList.toggle("is-open");
  }

  function closePopUpComponent() {
    popUpComponent.classList.remove("is-open");
  }

  if (!cartlist) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains('shoplist')) {
                cartlist = node;
                setupCartListeners();
                observer.disconnect();
                return;
              }
              const foundCart = node.querySelector && node.querySelector('.shoplist');
              if (foundCart) {
                cartlist = foundCart;
                setupCartListeners();
                observer.disconnect();
                return;
              }
            }
          });
        }
      });
    });
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function isElementValid(element, elementName) {
    if (!element) {
      console.warn(`${elementName} no está disponible`);
      return false;
    }
    return true;
  }

  function open() {
    try {
      if (!isElementValid(cartlist, 'Cart List')) return false;
      cartlist.classList.toggle("open-cart-list");
      document.body.classList.add('no-scroll');
      if (overlay) { overlay.classList.add('show'); }
      return true;
    } catch (error) {
      console.error('Error al abrir carrito:', error);
      return false;
    }
  }

  function close() {
    try {
      if (!isElementValid(cartlist, 'Cart List')) return false;
      cartlist.classList.remove("open-cart-list");
      document.body.classList.remove('no-scroll');
      if (overlay) { overlay.classList.remove('show'); }
      return true;
    } catch (error) {
      console.error('Error al cerrar carrito:', error);
      return false;
    }
  }

  try {
    document.getElementById('overlay').addEventListener('click', function() { close(); });
  } catch (error) { console.log(error); }

  const productHTML = (product_id, product_image, product_link, product_name, product_qty, product_price) => `
<div class="product product-${product_id}">
    <div class="product-image-space">
        <img class="product-image" loading="lazy" src="${product_image}" alt="Imagen de Producto">
    </div>
    <div class="product-description">
        <input name="id" id="product-item-cart-${product_id}" value="${product_id}" type="hidden" />
        <div>
          <a class="product-description-name" href="${product_link}">${product_name}</a>
        </div>
        <div class="product-description-info">
            <div class="product-description-all">
                <div class="product-description-qty">
                    <button type="button" id="remove-product-${product_id}" class="remove" data-product-id="${product_id}">-</button>
                    <input type="number" name="updates[]" min="1" step="1" value="${product_qty}" class="input-qty" id="input-qty-${product_id}" />
                    <button type="button" id="add-product-${product_id}" class="add" data-product-id="${product_id}">+</button>
                </div>
                <div class="loading-container" id="loading-container-${product_id}">
                    <div class="loading-bar"><div class="inner-bar"></div></div>
                </div>
                <span class="product-price-cart" id="product-price-${product_id}">$ ${product_price}</span>
                <button class="erase-product" type="button" data-product-id="${product_id}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path>
                        <path d="M10 11v6"></path><path d="M14 11v6"></path>
                        <path d="M1 6h22"></path><path d="M4 6l1-4h14l1 4"></path>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</div>
`;

  const recommendedProduct = (product_id, product_image, product_link, product_name, product_price, compare_at_price) => `
        <div class="new_recommended_product" id="product-${product_id}-recomended">
            <div class="">
                <input id="product-recommended-${product_id}" name="id" value="${product_id}" type="hidden" />
                <h3>
                    <a class="product-description-name recomended-text" id="product-name-${product_id}-recomended" href="${product_link}">${product_name}</a>
                </h3>
            </div>
            <div class="">
                <img class="product-image" id="product-image-${product_id}-recomended" loading="lazy" src="${product_image}" alt="Imagen de Producto">
            </div>
            <div class="footer__product">
                <div>
                    <span id="product-price-${product_id}-recomended">
                        <span class="compare-price-money">$ ${product_price}</span><br>
                        ${compare_at_price > 0 ? `<del class="price_to_compare">$ ${compare_at_price}</del>` : ''}
                    </span>
                </div>
                <div>
                    <input type="hidden" name="quantity" value="1" class="input-qty" id="input-qty-${product_id}-recomended"/>
                    <button type="button" data-product-id="${product_id}" class="add-product add-product-recommeded">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 68 64"><path d="M2.24 0.576c3.52-0.704 7.168 0.192 10.24 1.792 2.624 1.344 4.224 4.096 5.056 6.848 0.96 3.008 1.344 6.144 1.92 9.216 0.96 5.056 2.304 10.048 3.072 15.104 0.384 2.048 0.704 4.224 2.112 5.888 1.408 1.6 3.776 1.664 5.824 1.728 5.376 0 10.816-0.256 16.192 0.128 1.344 0.064 2.88 0 3.648-1.344 3.52-5.312 6.464-11.008 9.28-16.768 0.576-1.152 1.28-2.368 2.56-2.816 1.344-0.448 3.008-0.256 4.032 0.768 1.152 1.28 1.344 3.2 0.704 4.736-2.688 5.376-5.184 10.816-8.512 15.872-1.472 2.176-3.008 4.544-5.184 6.144-1.792 1.28-4.096 0.832-6.144 0.768-6.272-0.064-12.544 0.384-18.752 0-4.16-0.32-8.256-2.368-10.624-5.888-1.728-2.752-2.368-6.080-3.008-9.216-1.28-6.784-2.624-13.504-3.904-20.288-0.384-1.6-0.896-3.392-2.304-4.352-2.24-1.6-5.376-0.256-7.552-1.984-1.344-0.96-0.704-2.688-0.832-4.096-0.128-1.216 1.152-1.984 2.176-2.24zM34.432 4.928c-0.064-1.216 1.408-1.536 2.368-1.792 2.048-0.32 4.608-0.384 6.208 1.152 0.448 3.776 0.64 7.616 0.768 11.392 1.92 0.128 3.84-0.256 5.76-0.064 0.704 0.064 1.536 0.512 1.472 1.344-0.32 1.472-1.472 2.56-2.368 3.712-2.688 3.456-5.76 6.528-8.832 9.536-0.448 0.448-1.024 0.704-1.6 0.448-1.216-0.512-1.984-1.728-2.88-2.688-2.112-2.304-4.224-4.544-6.144-6.912-0.96-1.216-2.176-2.24-2.624-3.712-0.256-0.832 0.512-1.6 1.344-1.664 1.92-0.256 3.84 0.128 5.76 0 0.256-3.584 0.384-7.168 0.768-10.752zM25.28 51.584c2.432-0.704 5.184 0 6.784 1.856 2.368 2.624 1.984 7.232-0.96 9.216-3.136 2.304-8.448 1.216-9.92-2.56-1.664-3.328 0.64-7.616 4.096-8.512zM45.376 53.504c2.432-2.88 7.36-2.944 9.856-0.128 2.112 2.304 2.048 6.208-0.064 8.512-1.216 1.28-2.944 2.048-4.672 2.112h-0.128c-1.856-0.064-3.776-0.832-4.928-2.24-2.048-2.304-2.112-6.016-0.064-8.256z"></path></svg>
                    </button>
                </div>
            </div>
        </div>`;

  async function getAllCartData() {
    const response = await fetch(window.Shopify.routes.root + "cart.js", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return response.json();
  }

  function updateAllCart() {
    getAllCartData()
      .then((data) => {
        const items = data.items;
        const total_money = document.querySelector('.total-money');
        total_money.innerHTML = `$ ${(data.total_price / 100).toFixed(2)}`;

        window.FixOEMUpdateWholesaleProgress();

        const count = document.getElementById('cart-item-count-bubble');
        const total = document.getElementById('cart-total-price-bubble');
        if (count) { count.innerHTML = data.item_count; }

        // ✅ FIX: Actualizar badge .js-cart-item-count (móvil y escritorio)
        document.querySelectorAll('.js-cart-item-count').forEach(function(el) {
          el.textContent = data.item_count;
          el.style.display = data.item_count > 0 ? '' : 'none';
        });

        if (total) { total.innerHTML = `<span class="money">$ ${data.total_price/100} </span>`; }

        if (data.item_count == 0) {
          var section = document.querySelector(".product-list");
          var div = document.createElement("div");
          div.className = "text";
          div.textContent = "El carrito esta vacio";
          section.innerHTML = "";
          section.appendChild(div);
        } else {
          const product_list = document.querySelector('.product-list');
          product_list.innerHTML = "";
          items.forEach((item) => {
            console.log(item);
            const product_id = item.id;
            const product_image = item.image;
            const product_link = item.url;
            const product_name = item.title;
            const product_qty = item.quantity;
            const product_price = item.price;
            product_list.innerHTML += productHTML(product_id, product_image, product_link, product_name, product_qty, product_price / 100);
          });
          updateListeners();
        }
        if (typeof Samita !== "undefined" && typeof Samita.Wholesale !== "undefined") {
          Samita.Wholesale.initialize();
        }
      })
      .catch((error) => { console.error("Error:", error); });
  }

  async function getProductRecommendations(id) {
    const response = await fetch(window.Shopify.routes.root + `recommendations/products.json?product_id=${id}`);
    return response.json();
  }

  function getAllRecomendations() {
    getAllCartData().then((data) => {
      const recommendationsContainerOne = document.getElementById('dinamically-1');
      recommendationsContainerOne.innerHTML = "";
      if (data.item_count > 0) {
        const products = data.items;
        products.forEach((product) => {
          getProductRecommendations(product.product_id).then((relatedProducts) => {
            console.log(relatedProducts);
            const recommendation = relatedProducts.products;
            let AllRecomendations = [];
            recommendation.forEach((item) => {
              if (item.variants[0].available) { AllRecomendations.push(item); }
            });
            for (let i = 0; i < AllRecomendations.length; i++) {
              const product = AllRecomendations[i];
              let id = product.variants[0].id;
              let title = product.title;
              let price = product.price / 100;
              let url = product.url;
              let image = product.featured_image;
              let compare_price = product.variants[0].compare_at_price / 100;
              recommendationsContainerOne.innerHTML += recommendedProduct(id, image, url, title, price, compare_price);
              const recommendations = recommendationsContainerOne.children;
              for (let i = 0; i < recommendations.length; i++) {
                recommendations[i].style.display = i < 3 ? "flex" : "none";
              }
            }
            updateListenersRecommended();
          }).catch((error) => { console.error(error); });
        });
      } else if (data.item_count == 0) {
        getProductRecommendations(4609298104395).then((relatedProducts) => {
          const recommendation = relatedProducts.products;
          let AllRecomendations = [];
          recommendation.forEach((item) => {
            if (item.variants[0].available) { AllRecomendations.push(item); }
          });
          for (let i = 0; i < AllRecomendations.length; i++) {
            const product = AllRecomendations[i];
            let id = product.variants[0].id;
            let title = product.title;
            let price = product.price / 100;
            let url = product.url;
            let image = product.featured_image;
            let compare_price = product.variants[0].compare_at_price / 100;
            recommendationsContainerOne.innerHTML += recommendedProduct(id, image, url, title, price, compare_price);
            const recommendations = recommendationsContainerOne.children;
            for (let i = 0; i < recommendations.length; i++) {
              recommendations[i].style.display = i < 3 ? "flex" : "none";
            }
          }
          updateListenersRecommended();
        }).catch((error) => { console.error(error); });
      }
    }).catch((error) => { console.error(error); });
  }

  async function deleteCartElement(id) {
    let updates = { id: id, quantity: 0 };
    const response = await fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    updateAllCart();
    getAllRecomendations();
    return response.json();
  }

  async function addCartElement(id) {
    const qty = document.getElementById(`input-qty-${id}-recomended`);
    const formData = { items: [{ id: id, quantity: parseInt(qty.value) }] };
    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });
    updateAllCart();
    getAllRecomendations();
    return response.json();
  }

  async function addCartElementOne(id) {
    const formData = { items: [{ id: id, quantity: 1 }] };
    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });
    updateAllCart();
    getAllRecomendations();
    return response.json();
  }

  function updateCart(id) {
    const qtyInput = document.getElementById(`input-qty-${id}`);
    let updates = { id: id, quantity: parseInt(qtyInput.value) };
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: { [id]: updates.quantity } }),
    }).then((response) => { updateAllCart(); }).catch((error) => { console.error("Error:", error); });
  }

  function initSlider(slider, index) {
    const buttonNext = slider.querySelector('.next-recomended');
    const buttonBack = slider.querySelector('.back-recomended');
    const sliderBox = slider.querySelector('.recommended-products-container');
    const slides = sliderBox.children;
    let currentIndex = 1;
    buttonNext.addEventListener('click', function() { plusSlides(1); });
    buttonBack.addEventListener('click', function() { plusSlides(-1); });
    function plusSlides(n) { showSlides((currentIndex += n)); }
    function showSlides(n) {
      if (n > slides.length) { currentIndex = 1; }
      if (n < 1) { currentIndex = slides.length; }
      for (let i = 0; i < slides.length; i++) { slides[i].style.display = "none"; }
      slides[currentIndex - 1].style.display = "flex";
      slides[currentIndex].style.display = "flex";
      slides[currentIndex + 1].style.display = "flex";
    }
  }

  Array.from(document.getElementsByClassName('slider-cart')).forEach((slider, index) => {
    initSlider(slider, index);
  });

  function updateListeners() {
    Array.from(document.getElementsByClassName("add")).forEach((button) => {
      const product_id = button.getAttribute("data-product-id");
      button.addEventListener("click", function() {
        const tag = document.getElementById(`input-qty-${product_id}`);
        tag.value = parseInt(tag.value) + 1;
        updateCart(product_id);
        const loadingbar = document.getElementById(`loading-container-${product_id}`);
        loadingbar.style.display = "flex";
        setTimeout(() => { loadingbar.style.display = "none"; }, "500");
      });
    });

    Array.from(document.getElementsByClassName("remove")).forEach((button) => {
      const product_id = button.getAttribute("data-product-id");
      button.addEventListener("click", function() {
        const tag = document.getElementById(`input-qty-${product_id}`);
        if (tag.value >= 1) {
          tag.value = parseInt(tag.value) - 1;
          updateCart(product_id);
          const loadingbar = document.getElementById(`loading-container-${product_id}`);
          loadingbar.style.display = "flex";
          setTimeout(() => { loadingbar.style.display = "none"; }, "500");
        }
      });
    });

    Array.from(document.getElementsByClassName("erase-product")).forEach((button) => {
      const product_id = button.getAttribute("data-product-id");
      const product_to_erase = document.querySelector(`.product-${product_id}`);
      button.addEventListener("click", function() {
        deleteCartElement(product_id).then((data) => {
          if (product_to_erase) { product_to_erase.remove(); }
          setTimeout(() => { updateAllCart(); }, "1000");
        });
      });
    });
  }

  function updateListenersRecommended() {
    Array.from(document.getElementsByClassName("add-recomended")).forEach((button) => {
      const product_id = button.getAttribute("data-product-id");
      button.addEventListener("click", function() {
        const tag = document.getElementById(`input-qty-${product_id}-recomended`);
        tag.value = parseInt(tag.value) + 1;
      });
    });
    Array.from(document.getElementsByClassName("remove-recomended")).forEach((button) => {
      const product_id = button.getAttribute("data-product-id");
      button.addEventListener("click", function() {
        const tag = document.getElementById(`input-qty-${product_id}-recomended`);
        if (tag.value >= 1) { tag.value = parseInt(tag.value) - 1; }
      });
    });
    Array.from(document.getElementsByClassName("add-product-recommeded")).forEach((button) => {
      const product_id = button.getAttribute("data-product-id");
      button.addEventListener("click", function() {
        addCartElement(product_id).then((data) => { updateAllCart(); });
      });
    });
  }

  Array.from(document.getElementsByClassName("add-recomended-collection")).forEach((button) => {
    const product_id = button.getAttribute("data-product-id");
    button.addEventListener("click", function() {
      const tag = document.getElementById(`input-qty-${product_id}-recomended`);
      tag.value = parseInt(tag.value) + 1;
    });
  });

  Array.from(document.getElementsByClassName("remove-recomended-collection")).forEach((button) => {
    const product_id = button.getAttribute("data-product-id");
    button.addEventListener("click", function() {
      const tag = document.getElementById(`input-qty-${product_id}-recomended`);
      if (tag.value >= 1) { tag.value = parseInt(tag.value) - 1; }
    });
  });

  Array.from(document.getElementsByClassName("add-product-recommeded-colletion")).forEach((button) => {
    const product_id = button.getAttribute("data-product-id");
    button.addEventListener("click", function() {
      addCartElement(product_id).then((data) => { updateAllCart(); });
    });
  });

  console.log('cart-loaded');

  try {
    document.getElementById("add-product-one-banner").addEventListener("click", function() {
      addCartElementOne(48755820200226).then(() => {
        open();
        setTimeout(() => { updateAllCart(); getAllRecomendations(); }, 1000);
      });
    });
  } catch (error) { console.log(error); }

  try {
    document.getElementById("add-product-two-banner").addEventListener("click", function() {
      addCartElementOne(48755821019426).then(() => {
        open();
        setTimeout(() => { updateAllCart(); getAllRecomendations(); }, 1000);
      });
    });
  } catch (error) { console.log(error); }

  try {


document.getElementById("open-when-add").addEventListener("click", function() {
      openPopUpComponent();
    });
  } catch (error) { console.log(error); }

  try {
    document.querySelector(".btn-close").addEventListener("click", function() {
      closePopUpComponent();
    });
  } catch (error) { console.log(error); }

  function initializeCartListenerWithObserver() {
    const cartIcon = document.getElementById("cart-icon-bubble");
    if (cartIcon) {
      if (!cartIcon.hasAttribute('data-cart-listener')) {
        cartIcon.addEventListener("click", function() {
          console.log("Cart icon clicked!");
          updateAllCart();
          getAllRecomendations();
          open();
        });
        cartIcon.setAttribute('data-cart-listener', 'true');
        console.log("Event listener agregado exitosamente al cart-icon-bubble");
      }
      return;
    }
    const observer = new MutationObserver((mutations, obs) => {
      const cartIcon = document.getElementById("cart-icon-bubble");
      if (cartIcon && !cartIcon.hasAttribute('data-cart-listener')) {
        cartIcon.addEventListener("click", function() {
          console.log("Cart icon clicked!");
          updateAllCart();
          getAllRecomendations();
          open();
        });
        cartIcon.setAttribute('data-cart-listener', 'true');
        console.log("Event listener agregado exitosamente al cart-icon-bubble");
        obs.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      const cartIcon = document.getElementById("cart-icon-bubble");
      if (!cartIcon) { console.error("Timeout: Elemento cart-icon-bubble no encontrado después de 10 segundos"); }
    }, 10000);
  }

  function initializeCartListenerWithDelegation() {
    document.removeEventListener('click', handleCartClick);
    document.addEventListener('click', handleCartClick);
    function handleCartClick(event) {
      if (event.target.id === 'cart-icon-bubble' || event.target.closest('#cart-icon-bubble')) {
        console.log("Cart icon clicked via delegation!");
        updateAllCart();
        getAllRecomendations();
        open();
      }
    }
    console.log("Event delegation para cart-icon-bubble configurado");
  }

  function debugCartIcon() {
    const cartIcon = document.getElementById("cart-icon-bubble");
    console.log("Cart icon element:", cartIcon);
    console.log("Cart icon visible:", cartIcon ? window.getComputedStyle(cartIcon).display !== 'none' : 'Element not found');
    console.log("Cart icon clickable:", cartIcon ? !cartIcon.hasAttribute('disabled') : 'Element not found');
    if (cartIcon) {
      console.log("Cart icon classes:", cartIcon.classList.toString());
      console.log("Cart icon styles:", window.getComputedStyle(cartIcon));
    }
  }

  document.getElementById("close-cart").addEventListener("click", function() { close(); });

  try {
    Array.from(document.getElementsByClassName("open-when-add-quickview")).forEach((button) => {
      button.addEventListener("click", function() {
        open();
        setTimeout(() => { updateAllCart(); getAllRecomendations(); }, "1000");
      });
    });
  } catch (error) { console.log(error); }

  initializeCartListenerWithObserver();

  setTimeout(() => { debugCartIcon(); }, 2000);

});


document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('.shoplist');
  if (!drawer || drawer.dataset.fixoemRedesign === 'true') return;
  drawer.dataset.fixoemRedesign = 'true';
  drawer.classList.add('fixoem-cart-drawer');
  const title = drawer.querySelector('.title-cart h2');
  if (title) title.innerHTML = 'Mi carrito <span class="fixoem-cart-drawer__count" data-drawer-count></span>';
  const titleWrap = drawer.querySelector('.title-cart');
  if (titleWrap && !titleWrap.querySelector('.fixoem-cart-drawer__eyebrow')) title.insertAdjacentHTML('beforebegin', '<p class="fixoem-cart-drawer__eyebrow">Compra protegida</p>');
  const timer = drawer.querySelector('.timer');
  if (timer) { timer.style.display = 'flex'; timer.classList.add('fixoem-cart-drawer__progress'); }
  const recTitle = drawer.querySelector('.youmay');
  if (recTitle) recTitle.textContent = 'Completa tu reparación';
  const footer = drawer.querySelector('.cart-footer');
  if (footer) footer.innerHTML = '<div class="total-cart"><span class="total-label">Subtotal</span><span class="total-money">$0.00</span></div><p class="cart-advice">El envío se calcula con tu código postal al finalizar.</p><div class="fixoem-cart-drawer__trust"><span>Pago seguro</span><span>10 días de garantía</span><span>Envíos a todo México</span></div><div class="actions-cart"><button class="primary-button fixoem-cart-drawer__checkout" type="submit" name="checkout">Finalizar compra — <span data-checkout-total>$0.00</span></button><a href="/cart" class="primary-button fixoem-cart-drawer__view-cart">Ver y editar carrito</a></div>';
  const style = document.createElement('style');
  style.dataset.fixoemCartDrawer = 'true';
  style.textContent = ".fixoem-cart-drawer {\n  --fixoem-blue: #284899;\n  --fixoem-yellow: #fed700;\n  --fixoem-ink: #17203a;\n  --fixoem-muted: #647089;\n  --fixoem-border: #e6e9f0;\n  box-shadow: -12px 0 40px rgb(16 35 82 / 18%);\n  max-width: 480px;\n}\n\n.fixoem-cart-drawer .title-cart {\n  background: var(--fixoem-blue);\n  color: #fff;\n  height: auto;\n  min-height: 64px;\n  padding: 10px 18px;\n}\n\n.fixoem-cart-drawer .title-cart h2 {\n  color: #fff;\n  font-size: 18px;\n  line-height: 1.2;\n  margin: 2px 0 0;\n  padding: 0;\n}\n\n.fixoem-cart-drawer .title-cart button {\n  align-items: center;\n  color: #fff;\n  display: inline-flex;\n  justify-content: center;\n  min-height: 44px;\n  min-width: 44px;\n  padding: 0;\n}\n\n.fixoem-cart-drawer__eyebrow {\n  font-size: 10px;\n  font-weight: 700;\n  letter-spacing: .08em;\n  margin: 0;\n  opacity: .8;\n  text-transform: uppercase;\n}\n\n.fixoem-cart-drawer__count {\n  font-size: 13px;\n  font-weight: 500;\n  opacity: .8;\n}\n\n.fixoem-cart-drawer .products-cart-mix {\n  min-height: 0;\n  overflow-y: auto;\n}\n\n.fixoem-cart-drawer .product-list {\n  overflow: visible;\n  padding: 10px 16px 4px;\n}\n\n.fixoem-cart-drawer .product {\n  border-bottom: 1px solid var(--fixoem-border);\n  gap: 12px;\n  padding: 10px 0;\n}\n\n.fixoem-cart-drawer .product-image-space {\n  flex: 0 0 72px;\n  width: 72px;\n}\n\n.fixoem-cart-drawer .product-image {\n  aspect-ratio: 1;\n  border-radius: 8px;\n  object-fit: contain;\n  width: 100%;\n}\n\n.fixoem-cart-drawer .product-description {\n  gap: 7px;\n  width: auto;\n}\n\n.fixoem-cart-drawer .product-description-name {\n  color: var(--fixoem-ink);\n  font-size: 13px;\n  line-height: 1.35;\n}\n\n.fixoem-cart-drawer .product-description-name::after {\n  color: #178443;\n  content: \"Disponible\";\n  display: block;\n  font-size: 11px;\n  font-weight: 600;\n  margin-top: 3px;\n}\n\n.fixoem-cart-drawer .product-description-qty {\n  border-color: var(--fixoem-border);\n  border-radius: 7px;\n  height: 32px !important;\n}\n\n.fixoem-cart-drawer .input-qty {\n  width: 38px;\n}\n\n.fixoem-cart-drawer .erase-product {\n  min-height: 40px;\n  min-width: 40px;\n  opacity: .65;\n}\n\n.fixoem-cart-drawer__progress {\n  background: #f5f7fd;\n  border-bottom: 1px solid var(--fixoem-border);\n  display: flex;\n  gap: 7px;\n  min-height: auto;\n  padding: 9px 16px 10px;\n}\n\n.fixoem-cart-drawer__progress-copy {\n  display: flex;\n  justify-content: space-between;\n}\n\n.fixoem-cart-drawer .timer-message,\n.fixoem-cart-drawer #timer-message {\n  color: var(--fixoem-ink);\n  font-size: 11px;\n  line-height: 1.35;\n  text-align: left;\n}\n\n.fixoem-cart-drawer .progress-container {\n  background: #dfe5f4;\n  box-shadow: none;\n  height: 7px;\n  padding: 0;\n}\n\n.fixoem-cart-drawer .progress-bar {\n  background: var(--fixoem-yellow);\n  height: 7px;\n  line-height: 7px;\n}\n\n.fixoem-cart-drawer .recomendation {\n  border-top: 1px solid var(--fixoem-border);\n  min-height: 0;\n  padding: 10px 16px 12px;\n}\n\n.fixoem-cart-drawer .youmay-container {\n  background: transparent;\n  justify-content: flex-start;\n}\n\n.fixoem-cart-drawer .youmay {\n  color: var(--fixoem-ink);\n  font-size: 12px;\n  padding: 0 0 8px;\n}\n\n.fixoem-cart-drawer .slider-cart {\n  grid-template-columns: 24px minmax(0, 1fr) 24px;\n}\n\n.fixoem-cart-drawer .recommended-products-container {\n  gap: 8px;\n}\n\n.fixoem-cart-drawer .new_recommended_product {\n  border: 1px solid var(--fixoem-border);\n  border-radius: 8px;\n  height: auto;\n  min-height: 0;\n  padding: 8px;\n}\n\n.fixoem-cart-drawer .new_recommended_product:nth-child(n+3) {\n  display: none !important;\n}\n\n.fixoem-cart-drawer .new_recommended_product .product-image {\n  width: 46px;\n  height: 46px;\n  object-fit: contain;\n}\n\n.fixoem-cart-drawer .add-product-recommeded {\n  background: var(--fixoem-blue);\n  border: 0;\n  color: #fff;\n  height: 30px;\n  width: 30px;\n}\n\n.fixoem-cart-drawer .cart-footer {\n  background: #fff;\n  border-top: 1px solid var(--fixoem-border);\n  box-shadow: 0 -8px 22px rgb(16 35 82 / 8%);\n  flex: 0 0 auto;\n  padding: 10px 16px calc(12px + env(safe-area-inset-bottom));\n}\n\n.fixoem-cart-drawer .total-cart {\n  height: auto;\n  padding: 0;\n}\n\n.fixoem-cart-drawer .total-label {\n  color: var(--fixoem-ink);\n  font-size: 14px;\n}\n\n.fixoem-cart-drawer .total-money {\n  color: var(--fixoem-ink);\n  font-size: 19px;\n}\n\n.fixoem-cart-drawer .cart-advice {\n  color: var(--fixoem-muted);\n  font-size: 11px;\n  height: auto;\n  justify-content: flex-start;\n  margin: 3px 0 8px;\n}\n\n.fixoem-cart-drawer__trust {\n  color: var(--fixoem-blue);\n  display: flex;\n  font-size: 10px;\n  font-weight: 700;\n  gap: 6px;\n  justify-content: space-between;\n  margin-bottom: 9px;\n}\n\n.fixoem-cart-drawer__trust span {\n  align-items: center;\n  display: inline-flex;\n  gap: 4px;\n}\n\n.fixoem-cart-drawer__trust span::before {\n  color: #178443;\n  content: \"✓\";\n}\n\n.fixoem-cart-drawer .actions-cart {\n  align-items: stretch;\n  flex-direction: column;\n  gap: 7px;\n  height: auto;\n}\n\n.fixoem-cart-drawer .primary-button {\n  border-radius: 8px;\n  font-size: 14px;\n  height: 46px;\n  margin: 0;\n  width: 100%;\n}\n\n.fixoem-cart-drawer__checkout {\n  background: var(--fixoem-yellow) !important;\n  color: #101d42 !important;\n  font-weight: 800;\n  order: 1;\n}\n\n.fixoem-cart-drawer__view-cart {\n  background: #fff !important;\n  border: 1px solid var(--fixoem-blue) !important;\n  color: var(--fixoem-blue) !important;\n  height: 40px !important;\n  order: 2;\n}\n\n@media (max-width: 399px) {\n  .fixoem-cart-drawer {\n    width: 100vw;\n  }\n\n  .fixoem-cart-drawer__trust {\n    font-size: 9px;\n  }\n\n  .fixoem-cart-drawer .recomendation {\n    padding-inline: 12px;\n  }\n}\n";
  document.head.appendChild(style);
});

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('.fixoem-cart-drawer');
  if (!drawer) return;

  const updateDrawerSummary = async () => {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;

      const cart = await response.json();
      const total = cart.total_price / 100;
      const currency = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0
      });
      const formattedTotal = currency.format(total);

      drawer.querySelectorAll('.total-money, [data-checkout-total]').forEach((element) => {
        element.textContent = formattedTotal;
      });

      const count = drawer.querySelector('[data-drawer-count]');
      if (count) count.textContent = `— ${cart.item_count} ${cart.item_count === 1 ? 'producto' : 'productos'}`;

      // FixOEM: el mensaje/barra de "precio mayorista" ya NO se calcula aquí
      // con el umbral fijo de $4,500 -- se delega a la lógica real por
      // piezas/categoría (ver el bloque al inicio del archivo).
      window.FixOEMUpdateWholesaleProgress();
    } catch (error) {
      console.warn('No se pudo actualizar el resumen del carrito.', error);
    }
  };

  updateDrawerSummary();
  document.addEventListener('cart:updated', updateDrawerSummary);

  const observer = new MutationObserver(() => updateDrawerSummary());
  const productList = drawer.querySelector('.product-list');
  if (productList) observer.observe(productList, { childList: true, subtree: true });
});


// Keep checkout actions visible when the drawer contains many line items.
(() => {
  if (document.getElementById('fixoem-cart-drawer-scroll-fix')) return;
  const style = document.createElement('style');
  style.id = 'fixoem-cart-drawer-scroll-fix';
  style.textContent = `
    .fixoem-cart-drawer {
      display: flex !important;
      flex-direction: column !important;
      height: 100vh !important;
      height: 100dvh !important;
      max-height: 100vh !important;
      max-height: 100dvh !important;
      overflow: hidden !important;
    }
    .fixoem-cart-drawer > .title-cart,
    .fixoem-cart-drawer > .fixoem-cart-drawer__progress,
    .fixoem-cart-drawer > .recomendation,
    .fixoem-cart-drawer > .cart-footer {
      flex: 0 0 auto !important;
    }
    .fixoem-cart-drawer > .product-list {
      flex: 1 1 0% !important;
      min-height: 0 !important;
      overflow-x: hidden !important;
      overflow-y: auto !important;
      overscroll-behavior: contain !important;
      -webkit-overflow-scrolling: touch;
      scrollbar-gutter: stable;
    }
    .fixoem-cart-drawer > .cart-footer {
      position: relative !important;
      z-index: 20;
      background: #fff;
      box-shadow: 0 -8px 24px rgba(16, 35, 82, .10);
      padding-bottom: max(12px, env(safe-area-inset-bottom));
    }
  `;
  document.head.appendChild(style);
})();


// Compact Searchanise toolbar and keep price, rating and delivery on one row.
(() => {
  const styleId = 'fixoem-search-results-flow-v2';
  const installStyles = () => {
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      #snize_results > .snize-main-panel {
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
        width: 100% !important;
        min-height: 42px;
        padding: 0 10px !important;
        box-sizing: border-box;
      }
      #snize_results .snize-main-panel-controls { order: 1; flex: 0 0 auto; }
      #snize_results .fixoem-snize-result-summary {
        order: 2;
        flex: 1 1 auto;
        display: flex !important;
        align-items: center;
        min-width: 0;
        width: auto !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 8px !important;
        border: 0 !important;
        color: #38445d;
        font-size: 13px;
        line-height: 1.25;
      }
      #snize_results .snize-main-panel-dropdown { order: 3; flex: 0 0 auto; }
      #snize_results .snize-main-panel-view-mode { order: 4; flex: 0 0 auto; }
      #snize_results .fixoem-snize-meta-row {
        display: flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        gap: 7px !important;
        min-width: 0;
        width: 100%;
        min-height: 22px;
        margin: 6px 0 0 !important;
        overflow: hidden;
        white-space: nowrap;
      }
      #snize_results .fixoem-snize-meta-row .snize-price-list,
      #snize_results .fixoem-snize-meta-row .snize-custom-ud,
      #snize_results .fixoem-snize-meta-row .snize-reviews {
        display: inline-flex !important;
        align-items: center !important;
        flex: 0 0 auto !important;
        width: auto !important;
        min-height: 18px !important;
        margin: 0 !important;
        padding: 0 !important;
        float: none !important;
        line-height: 1 !important;
      }
      #snize_results .fixoem-snize-meta-row .snize-price { font-size: 18px !important; }
      #snize_results .fixoem-snize-meta-row .snize-reviews { order: 2; gap: 2px !important; font-size: 11px !important; }
      #snize_results .fixoem-snize-meta-row .snize-reviews:empty { display: none !important; }
      #snize_results .fixoem-snize-meta-row .snize-custom-ud { order: 3; font-size: 11px !important; }
      #snize_results .fixoem-snize-meta-row .snize-custom-ud__icon {
        width: 14px !important;
        height: 14px !important;
        flex: 0 0 14px !important;
        margin-right: 3px !important;
      }
      #snize_results .fixoem-snize-meta-row .snize-custom-ud__text { font-size: 11px !important; }
      @media (max-width: 767px) {
        #snize_results > .snize-main-panel { flex-wrap: wrap !important; gap: 6px 8px !important; padding: 6px 8px !important; }
        #snize_results .fixoem-snize-result-summary { order: 5; flex: 1 0 100%; padding: 0 !important; font-size: 12px; }
        #snize_results .fixoem-snize-meta-row { gap: 5px !important; }
        #snize_results .fixoem-snize-meta-row .snize-price { font-size: 17px !important; }
      }
    `;
    document.head.appendChild(style);
  };

  const enhanceSearchResults = () => {
    const root = document.querySelector('#snize_results');
    if (!root) return;

    const panel = root.querySelector(':scope > .snize-main-panel');
    const summary = root.querySelector(':scope > .snize-header');
    if (panel && summary) {
      summary.classList.add('fixoem-snize-result-summary');
      const dropdown = panel.querySelector(':scope > .snize-main-panel-dropdown');
      panel.insertBefore(summary, dropdown || null);
    }

    root.querySelectorAll('.snize-product .snize-overhidden').forEach((content) => {
      const price = content.querySelector(':scope > .snize-price-list');
      const reviews = content.querySelector(':scope > .snize-reviews');
      const delivery = content.querySelector(':scope > .snize-custom-ud');
      if (!price || !delivery) return;

      let row = content.querySelector(':scope > .fixoem-snize-meta-row');
      if (!row) {
        row = document.createElement('div');
        row.className = 'fixoem-snize-meta-row';
        content.insertBefore(row, price);
      }
      row.appendChild(price);
      if (reviews) row.appendChild(reviews);
      row.appendChild(delivery);
    });
  };

  const start = () => {
    installStyles();
    enhanceSearchResults();
    const observer = new MutationObserver(() => requestAnimationFrame(enhanceSearchResults));
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();



// FixOEM: sincronizacion robusta del carrito lateral (v3)
(function () {
  'use strict';

  if (window.FixOEMCartDrawerSyncV3) return;
  window.FixOEMCartDrawerSyncV3 = true;

  var syncing = false;
  var syncQueued = false;

  function rootPath() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return '$' + (((Number(cents) || 0) / 100).toFixed(2));
    }
  }

  function make(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  function updateSummary(drawer, cart) {
    var countText = '— ' + cart.item_count + (cart.item_count === 1 ? ' producto' : ' productos');
    var drawerCount = drawer.querySelector('[data-drawer-count]');
    if (drawerCount) drawerCount.textContent = countText;

    document.querySelectorAll('.js-cart-item-count').forEach(function (badge) {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? '' : 'none';
      var cartLink = badge.closest('a');
      if (cartLink) {
        cartLink.setAttribute('aria-label', 'Carrito, ' + cart.item_count + ' artículos');
      }
    });

    var legacyCount = document.getElementById('cart-item-count-bubble');
    if (legacyCount) legacyCount.textContent = cart.item_count;

    var formattedTotal = money(cart.total_price);
    drawer.querySelectorAll('.total-money, [data-checkout-total]').forEach(function (total) {
      total.textContent = formattedTotal;
    });

    var checkout = drawer.querySelector('[name="checkout"]');
    if (checkout) {
      checkout.disabled = cart.item_count === 0;
      checkout.setAttribute('aria-disabled', cart.item_count === 0 ? 'true' : 'false');
    }
  }

  function renderLine(item) {
    var row = make('div', 'product fixoem-cart-line');
    row.setAttribute('data-cart-line-key', item.key);

    var imageLink = make('a', 'product-image fixoem-cart-line__image');
    imageLink.href = item.url || '/cart';
    if (item.image) {
      var image = make('img');
      image.src = item.image;
      image.alt = item.product_title || item.title || 'Producto';
      image.loading = 'lazy';
      image.width = 72;
      image.height = 72;
      imageLink.appendChild(image);
    }

    var info = make('div', 'fixoem-cart-line__info');
    var title = make('a', 'product-name fixoem-cart-line__title', item.product_title || item.title || 'Producto');
    title.href = item.url || '/cart';
    info.appendChild(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      info.appendChild(make('div', 'fixoem-cart-line__variant', item.variant_title));
    }

    info.appendChild(make('div', 'fixoem-cart-line__availability', 'Disponible'));

    var controls = make('div', 'fixoem-cart-line__controls');
    controls.appendChild(make('strong', 'fixoem-cart-line__price', money(item.final_line_price)));

    var quantity = make('div', 'fixoem-cart-qty');
    var minus = make('button', 'fixoem-cart-qty__button', '−');
    minus.type = 'button';
    minus.setAttribute('aria-label', 'Reducir cantidad');
    minus.setAttribute('data-fixoem-cart-action', 'decrease');
    minus.setAttribute('data-cart-key', item.key);
    minus.setAttribute('data-cart-quantity', item.quantity);

    var amount = make('span', 'fixoem-cart-qty__value', String(item.quantity));
    amount.setAttribute('aria-label', 'Cantidad ' + item.quantity);

    var plus = make('button', 'fixoem-cart-qty__button', '+');
    plus.type = 'button';
    plus.setAttribute('aria-label', 'Aumentar cantidad');
    plus.setAttribute('data-fixoem-cart-action', 'increase');
    plus.setAttribute('data-cart-key', item.key);
    plus.setAttribute('data-cart-quantity', item.quantity);

    quantity.appendChild(minus);
    quantity.appendChild(amount);
    quantity.appendChild(plus);
    controls.appendChild(quantity);
    info.appendChild(controls);

    var remove = make('button', 'fixoem-cart-line__remove', 'Eliminar');
    remove.type = 'button';
    remove.setAttribute('data-fixoem-cart-action', 'remove');
    remove.setAttribute('data-cart-key', item.key);
    remove.setAttribute('aria-label', 'Eliminar ' + (item.product_title || item.title || 'producto'));
    info.appendChild(remove);

    row.appendChild(imageLink);
    row.appendChild(info);
    return row;
  }

  function renderCart(cart) {
    var drawer = document.querySelector('.shoplist.fixoem-cart-drawer');
    if (!drawer) return false;

    var productList = drawer.querySelector('.product-list');
    if (!productList) return false;

    productList.replaceChildren();

    if (!cart.items || cart.items.length === 0) {
      var empty = make('div', 'fixoem-cart-empty');
      empty.appendChild(make('strong', '', 'Tu carrito está vacío'));
      empty.appendChild(make('p', '', 'Agrega una refacción y aparecerá aquí.'));
      productList.appendChild(empty);
    } else {
      cart.items.forEach(function (item) {
        productList.appendChild(renderLine(item));
      });
    }

    updateSummary(drawer, cart);
    // FixOEM: este render reconstruye el carrito por su cuenta -- hay que
    // recalcular el banner de mayoreo aquí también, si no, se queda con lo
    // que haya quedado de la última vez (o vacío) hasta que otro de los
    // scripts de este archivo lo dispare.
    if (window.FixOEMUpdateWholesaleProgress) window.FixOEMUpdateWholesaleProgress();
    drawer.setAttribute('data-cart-synced', 'true');
    return true;
  }

  async function fetchCart() {
    var response = await fetch(rootPath() + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Cart request failed: ' + response.status);
    return response.json();
  }

  async function syncCart() {
    if (syncing) {
      syncQueued = true;
      return;
    }

    syncing = true;
    try {
      var cart = await fetchCart();
      renderCart(cart);
    } catch (error) {
      console.warn('[FixOEM] No fue posible sincronizar el carrito lateral.', error);
    } finally {
      syncing = false;
      if (syncQueued) {
        syncQueued = false;
        syncCart();
      }
    }
  }

  async function changeQuantity(key, quantity) {
    var response = await fetch(rootPath() + 'cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    });

    if (!response.ok) throw new Error('Cart change failed: ' + response.status);
    var cart = await response.json();
    renderCart(cart);
  }

  function installStyles() {
    if (document.getElementById('fixoem-cart-sync-v3-style')) return;
    var style = document.createElement('style');
    style.id = 'fixoem-cart-sync-v3-style';
    style.textContent = [
      '.fixoem-cart-drawer .fixoem-cart-line{display:grid!important;grid-template-columns:72px minmax(0,1fr);gap:12px;align-items:start;padding:12px 16px!important;border-bottom:1px solid #e4e8ef;}',
      '.fixoem-cart-drawer .fixoem-cart-line__image{display:block;width:72px;height:72px;border:1px solid #e4e8ef;border-radius:10px;overflow:hidden;background:#fff;}',
      '.fixoem-cart-drawer .fixoem-cart-line__image img{width:100%;height:100%;object-fit:contain;}',
      '.fixoem-cart-drawer .fixoem-cart-line__info{min-width:0;}',
      '.fixoem-cart-drawer .fixoem-cart-line__title{display:block;color:#0b1f43;font-weight:700;line-height:1.25;text-decoration:none;}',
      '.fixoem-cart-drawer .fixoem-cart-line__variant{margin-top:3px;color:#68758a;font-size:12px;}',
      '.fixoem-cart-drawer .fixoem-cart-line__availability{margin-top:4px;color:#07884a;font-size:12px;font-weight:700;}',
      '.fixoem-cart-drawer .fixoem-cart-line__controls{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:8px;}',
      '.fixoem-cart-drawer .fixoem-cart-line__price{color:#071f49;font-size:16px;}',
      '.fixoem-cart-drawer .fixoem-cart-qty{display:inline-flex;align-items:center;border:1px solid #cfd6e3;border-radius:999px;overflow:hidden;background:#fff;}',
      '.fixoem-cart-drawer .fixoem-cart-qty__button{width:30px;height:28px;border:0;background:#fff;color:#123b8f;font-size:18px;line-height:1;cursor:pointer;}',
      '.fixoem-cart-drawer .fixoem-cart-qty__value{min-width:26px;text-align:center;font-weight:700;font-size:13px;}',
      '.fixoem-cart-drawer .fixoem-cart-line__remove{margin-top:7px;padding:0;border:0;background:transparent;color:#68758a;font-size:12px;text-decoration:underline;cursor:pointer;}',
      '.fixoem-cart-drawer .fixoem-cart-empty{padding:28px 18px;text-align:center;color:#0b1f43;}',
      '.fixoem-cart-drawer .fixoem-cart-empty p{margin:6px 0 0;color:#68758a;}'
    ].join('');
    document.head.appendChild(style);
  }

  document.addEventListener('click', function (event) {
    var actionButton = event.target.closest('[data-fixoem-cart-action]');
    if (actionButton) {
      event.preventDefault();
      var action = actionButton.getAttribute('data-fixoem-cart-action');
      var key = actionButton.getAttribute('data-cart-key');
      var current = Number(actionButton.getAttribute('data-cart-quantity')) || 0;
      var next = action === 'remove' ? 0 : current + (action === 'increase' ? 1 : -1);
      actionButton.disabled = true;
      changeQuantity(key, next).catch(function (error) {
        console.warn('[FixOEM] No fue posible actualizar el artículo.', error);
        syncCart();
      });
      return;
    }

    var opener = event.target.closest('#cart-icon-bubble, .header-cart, [data-cart-drawer-trigger]');
    if (opener) syncCart();
  }, true);

  window.addEventListener('pageshow', syncCart);
  document.addEventListener('cart:updated', syncCart);
  document.addEventListener('ajaxCart:updated', syncCart);
  document.addEventListener('shopify:section:load', syncCart);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') syncCart();
  });

  function start() {
    installStyles();
    syncCart();
    window.setTimeout(syncCart, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


(function () {
  if (window.FixOEMHeaderCarrierBadgesV1) return;
  window.FixOEMHeaderCarrierBadgesV1 = true;

  var STYLE_ID = 'fixoem-header-carrier-badges';

  function installStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '.fixoem-promo-extra-row,.fixoem-desktop-carriers{display:flex;align-items:center;justify-content:center;}',
      '.fixoem-promo-extra-row{gap:8px;width:100%;}',
      '.fixoem-desktop-carriers{gap:12px;flex-shrink:0;}',
      '.fixoem-pickup-badge{display:inline-flex;align-items:center;gap:3px;color:#102a56;font-size:9px;font-weight:800;line-height:1;white-space:nowrap;}',
      '.fixoem-pickup-pin{display:inline-grid;place-items:center;width:12px;height:12px;border-radius:50%;background:#e31b23;color:#fff;font-size:8px;line-height:1;}',
      '.fixoem-carrier-wordmark{display:inline-flex;align-items:baseline;white-space:nowrap;line-height:1;font-family:Arial,Helvetica,sans-serif;}',
      '.fixoem-fedex{font-size:15px;font-weight:900;letter-spacing:-1.4px;}',
      '.fixoem-fedex-fed{color:#4d148c;}.fixoem-fedex-ex{color:#ff6600;}',
      '.fixoem-estafeta{color:#d71920;font-size:12px;font-weight:900;font-style:italic;letter-spacing:-.7px;}',
      '.fixoem-pickup-desktop{display:flex!important;align-items:center!important;}',
      '@media (max-width:991.98px){',
        'html body .fixoem-promo-pill-mobile{display:flex!important;flex-wrap:wrap!important;align-content:center!important;justify-content:center!important;column-gap:10px!important;row-gap:1px!important;min-width:146px!important;max-width:160px!important;height:50px!important;overflow:visible!important;}',
        'html body .fixoem-promo-pill-mobile>.shipping-item{height:28px!important;}',
        'html body .fixoem-promo-pill-mobile>.shipping-item img{height:28px!important;max-height:28px!important;width:auto!important;}',
        'html body .fixoem-promo-pill-mobile>.fixoem-promo-extra-row{display:flex!important;flex:0 0 100%!important;height:15px!important;}',
        'html body .header-mobile_top{align-items:center!important;min-height:58px!important;}',
      '}',
      '@media (min-width:992px){',
        '.header-shipping-methods .fixoem-pickup-badge{font-size:11px;}',
        '.header-shipping-methods .fixoem-fedex{font-size:17px;}',
        '.header-shipping-methods .fixoem-estafeta{font-size:14px;}',
      '}'
    ].join('');

    document.head.appendChild(style);
  }

  function pickupMark() {
    return '<span class="fixoem-pickup-badge" aria-label="Recoge en tienda"><span class="fixoem-pickup-pin" aria-hidden="true">•</span><span>Recoge en tienda</span></span>';
  }

  function carrierMarks() {
    return '<span class="fixoem-carrier-wordmark fixoem-fedex" aria-label="FedEx"><span class="fixoem-fedex-fed">Fed</span><span class="fixoem-fedex-ex">Ex</span></span>' +
      '<span class="fixoem-carrier-wordmark fixoem-estafeta" aria-label="Estafeta">estafeta</span>';
  }

  function installBadges() {
    document.querySelectorAll('.fixoem-promo-pill-mobile').forEach(function (pill) {
      if (pill.querySelector('.fixoem-promo-extra-row')) return;
      pill.insertAdjacentHTML('beforeend', '<div class="fixoem-promo-extra-row" aria-label="Opciones de entrega">' + pickupMark() + carrierMarks() + '</div>');
    });

    document.querySelectorAll('.header-shipping-methods:not(.fixoem-promo-pill-mobile)').forEach(function (pill) {
      var store = pill.querySelector('.shipping-store');
      if (store && !store.classList.contains('fixoem-pickup-desktop')) {
        store.classList.add('fixoem-pickup-desktop');
        store.setAttribute('aria-label', 'Recoge en tienda');
        store.innerHTML = pickupMark();
      }

      if (!pill.querySelector('.fixoem-desktop-carriers')) {
        pill.insertAdjacentHTML('beforeend', '<div class="shipping-item fixoem-desktop-carriers" aria-label="Paqueterías">' + carrierMarks() + '</div>');
      }
    });
  }

  function start() {
    installStyles();
    installBadges();
    window.setTimeout(installBadges, 300);
    window.setTimeout(installBadges, 1200);
    window.setTimeout(installBadges, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', installBadges);
})();


(function () {
  if (document.getElementById('fixoem-header-carrier-badges-force')) return;

  var style = document.createElement('style');
  style.id = 'fixoem-header-carrier-badges-force';
  style.textContent = [
    '@media (max-width:991.98px){',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile{display:flex!important;flex-wrap:wrap!important;align-content:center!important;justify-content:center!important;column-gap:10px!important;row-gap:1px!important;min-width:146px!important;max-width:160px!important;height:52px!important;min-height:52px!important;overflow:visible!important;}',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile>.shipping-item{height:28px!important;}',
      'html body .header-mobile .header-mobile-top .fixoem-promo-pill-mobile>.shipping-item img,html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile>.shipping-item img{height:28px!important;max-height:28px!important;width:auto!important;}',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile>.fixoem-promo-extra-row{display:flex!important;flex:0 0 100%!important;width:100%!important;height:16px!important;gap:6px!important;overflow:visible!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-badge{font-size:8px!important;gap:3px!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-badge>span:last-child{display:inline-block!important;width:34px!important;white-space:normal!important;line-height:.85!important;text-align:left!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-fedex{font-size:13px!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-estafeta{font-size:10.5px!important;}',
      'html body .header-mobile .header-mobile_top{min-height:60px!important;}',
    '}'
  ].join('');

  document.head.appendChild(style);
})();


(function () {
  if (document.getElementById('fixoem-header-mobile-one-line')) return;

  var style = document.createElement('style');
  style.id = 'fixoem-header-mobile-one-line';
  style.textContent = [
    '@media (max-width:991.98px){',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile{display:flex!important;flex-wrap:nowrap!important;align-items:center!important;align-content:center!important;justify-content:flex-end!important;gap:5px!important;min-width:190px!important;max-width:190px!important;width:190px!important;height:34px!important;min-height:34px!important;overflow:visible!important;}',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile>.shipping-item{display:flex!important;height:22px!important;flex:0 0 auto!important;}',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile>.shipping-item img{height:22px!important;max-height:22px!important;width:auto!important;}',
      'html body .header-mobile .header-mobile_top .fixoem-promo-pill-mobile>.fixoem-promo-extra-row{display:contents!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-badge{display:inline-flex!important;gap:2px!important;font-size:0!important;flex:0 0 auto!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-pin{width:10px!important;height:10px!important;font-size:0!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-pin:after{content:"⌂";font-size:8px!important;line-height:1!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-badge>span:last-child{display:inline!important;width:auto!important;white-space:nowrap!important;font-size:0!important;line-height:1!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-pickup-badge>span:last-child:after{content:"Tienda";font-size:7px!important;line-height:1!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-fedex{font-size:11px!important;letter-spacing:-1px!important;flex:0 0 auto!important;}',
      'html body .header-mobile .fixoem-promo-extra-row .fixoem-estafeta{font-size:8.5px!important;letter-spacing:-.5px!important;flex:0 0 auto!important;}',
      'html body .header-mobile .header-mobile_top{min-height:54px!important;}',
    '}'
  ].join('');

  document.head.appendChild(style);
})();

/* FixOEM Cart Live Sync V1 - mantiene el drawer alineado con Shopify */
(function () {
  if (window.__fixoemCartLiveSyncV1) return;
  window.__fixoemCartLiveSyncV1 = true;

  var nativeFetch = window.fetch.bind(window);
  var nativeXHROpen = XMLHttpRequest.prototype.open;
  var nativeXHRSend = XMLHttpRequest.prototype.send;
  var syncTimer;

  function isCartMutation(url, method) {
    return method !== 'GET' && /\/cart\/(add|change|update)(?:\.js)?(?:[?#]|$)/i.test(String(url || ''));
  }

  function scheduleCartSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncCartDrawer, 350);
  }

  async function syncCartDrawer() {
    try {
      var cartResponse = await nativeFetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
      });
      if (!cartResponse.ok) throw new Error('No se pudo leer /cart.js');
      var cart = await cartResponse.json();

      var separator = window.location.search ? '&' : '?';
      var pageResponse = await nativeFetch(
        window.location.pathname + window.location.search + separator + '_fixoem_cart_sync=' + Date.now(),
        {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Cache-Control': 'no-cache' }
        }
      );
      if (!pageResponse.ok) throw new Error('No se pudo actualizar el drawer');

      var parsed = new DOMParser().parseFromString(await pageResponse.text(), 'text/html');
      var currentDrawer = document.querySelector('.fixoem-cart-drawer');
      var freshDrawer = parsed.querySelector('.fixoem-cart-drawer');

      if (currentDrawer && freshDrawer) {
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        currentDrawer.setAttribute('data-cart-synced', 'true');
      }

      document.querySelectorAll('.js-cart-item-count').forEach(function (badge) {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? '' : 'none';
      });

      document.querySelectorAll('.fx-bnav__item--cart').forEach(function (link) {
        link.setAttribute('aria-label', 'Carrito, ' + cart.item_count + ' artículos');
      });

      // FixOEM: este flujo reemplaza el innerHTML COMPLETO del drawer con lo
      // que devuelva el servidor -- eso incluye el mensaje/barra de mayoreo
      // renderizado por Liquid, que no sabe nada de piezas por categoría. Se
      // recalcula aquí para que quede correcto también después de este tipo
      // de sincronización.
      if (window.FixOEMUpdateWholesaleProgress) window.FixOEMUpdateWholesaleProgress();

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart, source: 'fixoem-live-sync' }
      }));
    } catch (error) {
      console.warn('FixOEM cart live sync:', error);
    }
  }

  window.fetch = function () {
    var args = Array.prototype.slice.call(arguments);
    var request = args[0];
    var options = args[1] || {};
    var url = typeof request === 'string' ? request : (request && request.url) || '';
    var method = String(options.method || (request && request.method) || 'GET').toUpperCase();
    var result = nativeFetch.apply(window, args);

    if (isCartMutation(url, method)) {
      result.then(function (response) {
        if (response && response.ok) scheduleCartSync();
      }).catch(function () {});
    }
    return result;
  };

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__fixoemCartMutation = isCartMutation(url, String(method || 'GET').toUpperCase());
    return nativeXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.__fixoemCartMutation) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 400) scheduleCartSync();
      });
    }
    return nativeXHRSend.apply(this, arguments);
  };

  document.addEventListener('cart:refresh', scheduleCartSync);
  document.addEventListener('product:added', scheduleCartSync);
})();


/* FixOEM Search Add-to-Cart Trigger V1 */
(function () {
  if (window.__fixoemCartClickSyncV1) return;
  window.__fixoemCartClickSyncV1 = true;

  function requestSync() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  document.addEventListener('click', function (event) {
    var control = event.target && event.target.closest
      ? event.target.closest('button, a, [role="button"]')
      : null;
    if (!control) return;

    var label = ((control.getAttribute('aria-label') || '') + ' ' + (control.textContent || '')).toLowerCase();
    var isAddButton = /agregar(?: ahora)?(?: al)? carrito|add to cart/.test(label) ||
      control.matches('[name="add"], [data-add-to-cart], .product-form__submit');

    if (isAddButton) {
      window.setTimeout(requestSync, 700);
      window.setTimeout(requestSync, 1400);
    }
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form && /\/cart\/add/i.test(form.getAttribute('action') || '')) {
      window.setTimeout(requestSync, 700);
      window.setTimeout(requestSync, 1400);
    }
  }, true);
})();


/* FixOEM Cart Badge Observer V1 */
(function () {
  if (window.__fixoemCartBadgeObserverV1) return;
  window.__fixoemCartBadgeObserverV1 = true;
  document.documentElement.setAttribute('data-fixoem-cart-sync', 'observer-v1');

  var observerFetch = window.fetch.bind(window);
  var observerTimer;
  var observerBusy = false;
  // FixOEM fix: durante los primeros 2.5s después de cargar la página, este
  // observer se queda callado -- en esa ventana FixOEMCartDrawerSyncV3 ya se
  // está encargando de pintar el drawer (syncCart() al cargar + a los 1200ms),
  // y dejar que ambos reescriban el mismo elemento al mismo tiempo era la
  // causa del parpadeo/flash visual al abrir la página.
  var pageJustLoaded = true;
  window.setTimeout(function () { pageJustLoaded = false; }, 2500);

  function numberFrom(element) {
    var match = ((element && element.textContent) || '').match(/\d+/);
    return match ? Number(match[0]) : 0;
  }

  async function refreshFromServer() {
    if (observerBusy) return;
    observerBusy = true;
    try {
      var separator = window.location.search ? '&' : '?';
      var response = await observerFetch(
        window.location.pathname + window.location.search + separator + '_fixoem_badge_sync=' + Date.now(),
        { credentials: 'same-origin', headers: { 'Cache-Control': 'no-cache' } }
      );
      if (!response.ok) return;
      var parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      var current = document.querySelector('.fixoem-cart-drawer');
      var fresh = parsed.querySelector('.fixoem-cart-drawer');
      if (current && fresh) current.innerHTML = fresh.innerHTML;
      // FixOEM: mismo caso que en FixOEMCartLiveSyncV1 -- este observer
      // también reemplaza el drawer completo desde el servidor, así que hay
      // que recalcular el banner de mayoreo real después.
      if (window.FixOEMUpdateWholesaleProgress) window.FixOEMUpdateWholesaleProgress();
    } catch (error) {
      console.warn('FixOEM cart badge observer:', error);
    } finally {
      observerBusy = false;
    }
  }

  function compareCounts() {
    if (pageJustLoaded) return;
    var badgeCount = numberFrom(document.querySelector('.js-cart-item-count'));
    var drawerCount = numberFrom(document.querySelector('.fixoem-cart-drawer__count'));
    if (badgeCount !== drawerCount) {
      window.clearTimeout(observerTimer);
      observerTimer = window.setTimeout(refreshFromServer, 450);
    }
  }

  new MutationObserver(compareCounts).observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true
  });
})();

/* ============================================================
   FixOEM — Conversión visual a COP en el carrito (solo Colombia)
   Informativo únicamente; el cobro real sigue en MXN.
   Actualiza FX_RATE periódicamente según el tipo de cambio.
   ============================================================ */
(function () {
  if (typeof window === 'undefined') return;
  if (window.FX_MARKET_HANDLE !== 'colombia') return;

  var FX_RATE = 180.5; // 1 MXN = X COP (referencia ago 2026)

  var PRICE_SELECTOR = [
    '.total-money',
    '[data-checkout-total]',
    '.product-price-cart',
    '.fixoem-cart-line__price',
    '[id^="product-price-"]:not([id*="recomended"])'
  ].join(', ');

  function parseMXN(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^0-9.,]/g, '');
    if (!cleaned) return null;
    // Formato MX: coma = miles, punto = decimales
    cleaned = cleaned.replace(/,/g, '');
    var value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
  }

  function formatCOP(value) {
    var rounded = Math.round(value);
    var str = String(rounded);
    var withDots = '';
    for (var i = 0; i < str.length; i++) {
      var posFromEnd = str.length - i;
      withDots += str[i];
      if (posFromEnd > 1 && posFromEnd % 3 === 1) withDots += '.';
    }
    return withDots;
  }

  function ensureNote(priceEl) {
    var next = priceEl.nextElementSibling;
    if (next && next.classList && next.classList.contains('fx-cop-note')) {
      return next;
    }
    var note = document.createElement('span');
    note.className = 'fx-cop-note';
    note.setAttribute('data-fx-cop-note', '1');
    priceEl.insertAdjacentElement('afterend', note);
    return note;
  }

  function syncCOP() {
    var elements = document.querySelectorAll(PRICE_SELECTOR);
    elements.forEach(function (el) {
      if (el.classList && el.classList.contains('fx-cop-note')) return;
      if (el.hasAttribute('data-fx-cop-note')) return;
      var mxn = parseMXN(el.textContent);
      if (mxn === null) return;
      var cop = mxn * FX_RATE;
      var note = ensureNote(el);
      note.textContent = '≈ $' + formatCOP(cop) + ' COP';
    });
  }

  var scheduled = false;
  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      syncCOP();
    }, 80);
  }

  var style = document.createElement('style');
  style.textContent = '.fx-cop-note{display:block;font-size:11px;font-weight:600;color:#59657a;margin-top:2px;}';
  document.head.appendChild(style);

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'childList' || m.type === 'characterData') {
        scheduleSync();
        break;
      }
    }
  });

  function start() {
    syncCOP();
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();